import { Link } from "react-router-dom";
import { Building2, MapPin, Phone, Mail, Facebook, Instagram, ArrowRight } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Separator } from "../components/ui/separator";

export default function Footer() {
  return (
    <footer data-testid="main-footer" className="bg-slate-navy text-white">
      {/* Newsletter */}
      <div className="border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div>
              <h3 className="font-heading text-2xl font-semibold">Stay Updated</h3>
              <p className="text-slate-400 mt-1">Get the latest property listings and market insights</p>
            </div>
            <div className="flex w-full md:w-auto gap-2">
              <Input
                data-testid="newsletter-email-input"
                placeholder="Enter your email"
                className="bg-white/10 border-white/20 text-white placeholder:text-slate-400 rounded-xl w-full md:w-72"
              />
              <Button data-testid="newsletter-submit-btn" className="bg-terracotta hover:bg-terracotta-hover text-white rounded-xl px-6 whitespace-nowrap">
                Subscribe <ArrowRight className="w-4 h-4 ml-1.5" />
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Footer */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">
          {/* Brand */}
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-terracotta rounded-lg flex items-center justify-center">
                <Building2 className="w-5 h-5 text-white" />
              </div>
              <span className="font-heading font-bold text-xl">
                Slab<span className="text-terracotta">Story</span>
              </span>
            </div>
            <p className="text-slate-400 text-sm leading-relaxed">
              Where Transparency Builds Trust. Hyderabad's most trusted platform for verified property listings.
            </p>
            <div className="flex gap-3">
              {[
                { icon: Facebook, label: "Facebook", href: "https://www.facebook.com/profile.php?id=61588183900997" },
                { icon: Instagram, label: "Instagram", href: "https://www.instagram.com/slabstory.in" },
              ].map((social) => (
                <a
                  key={social.label}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`footer-${social.label.toLowerCase()}`}
                  className="w-9 h-9 bg-white/10 rounded-lg flex items-center justify-center hover:bg-terracotta transition-colors"
                >
                  <social.icon className="w-4 h-4" />
                </a>
              ))}
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-heading font-semibold mb-4">Quick Links</h4>
            <ul className="space-y-2.5">
              {[
                { label: "Buy Property", href: "/properties" },
                { label: "List Your Property", href: "/contact" },
                { label: "About Us", href: "/about" },
                { label: "Contact", href: "/contact" },
                { label: "EMI Calculator", href: "/home-loan-calculator" },
              ].map((link) => (
                <li key={link.label}>
                  <Link to={link.href} className="text-slate-400 text-sm hover:text-terracotta transition-colors">
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Property Types */}
          <div>
            <h4 className="font-heading font-semibold mb-4">Property Types</h4>
            <ul className="space-y-2.5">
              {["2 BHK Apartments", "3 BHK Apartments", "4 BHK Apartments", "5 BHK Ultra Luxury Sky Villas", "Villas & Houses", "Open Plots"].map(
                (type) => (
                  <li key={type}>
                    <Link to="/properties" className="text-slate-400 text-sm hover:text-terracotta transition-colors">
                      {type}
                    </Link>
                  </li>
                )
              )}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="font-heading font-semibold mb-4">Contact Us</h4>
            <ul className="space-y-3">
              <li className="flex items-start gap-2.5">
                <MapPin className="w-4 h-4 text-terracotta mt-0.5 shrink-0" />
                <span className="text-slate-400 text-sm">Hyderabad, Telangana, India</span>
              </li>
              <li className="flex items-center gap-2.5">
                <Phone className="w-4 h-4 text-terracotta shrink-0" />
                <span className="text-slate-400 text-sm">+91 98490 72001</span>
              </li>
              <li className="flex items-center gap-2.5">
                <Mail className="w-4 h-4 text-terracotta shrink-0" />
                <span className="text-slate-400 text-sm">hello@slabstory.in</span>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <Separator className="bg-white/10" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5">
        <div className="flex flex-col md:flex-row items-center justify-between gap-3 text-sm text-slate-500">
          <p>&copy; {new Date().getFullYear()} SlabStory.in. All rights reserved.</p>
          <div className="flex gap-4">
            <a href="#" className="hover:text-slate-300 transition-colors">Privacy Policy</a>
            <a href="#" className="hover:text-slate-300 transition-colors">Terms of Service</a>
          </div>
        </div>
      </div>
    </footer>
  );
}
